import moment from "moment";
import { isEqual } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { IBranch, IScheduledService, PickupStatus } from "../../types/shared";
import {
  AuditAction,
  BranchSelectionMap,
  DisableDurationPreset,
  IAuditLogPayload,
  IScheduledServiceDisable,
  IServiceDisableMetadata,
  ServiceType,
} from "../../types/audit";

const EXCLUDED_KEYS = ["image"];
export const getChangedFields = <T extends Record<string, unknown>>(
  before: T,
  after: T,
  exclude: string[] = EXCLUDED_KEYS
): { before: Partial<T>; after: Partial<T> } => {
  const changedBefore: Partial<T> = {};
  const changedAfter: Partial<T> = {};
  for (const key of Object.keys(after)) {
    if (exclude.includes(key)) continue;
    if (after[key] === undefined) continue;
    const beforeVal = before[key];
    if (beforeVal === undefined) continue;
    let afterVal = after[key];
    if (typeof afterVal === "string" && /^\d+(\.\d+)?$/.test(afterVal) && typeof beforeVal === "number") {
      afterVal = Number(afterVal) as any;
    }
    if (Array.isArray(afterVal)) continue;
    if (!isEqual(beforeVal, afterVal)) {
      const typedKey = key as keyof T;
      changedBefore[typedKey] = beforeVal as T[keyof T];
      changedAfter[typedKey] = afterVal as T[keyof T];
    }
  }
  return { before: changedBefore, after: changedAfter };
};

const SERVICE_MAP = {
  pickup: { selectionKey: "pickup_branch_ids" as const, statusKey: "pickup_status" as const },
  delivery: { selectionKey: "delivery_branch_ids" as const, statusKey: "delivery_status" as const },
  drive_thru: { selectionKey: "drive_thru_branch_ids" as const, statusKey: "drive_thru_status" as const },
  offline_loyalty: { selectionKey: "offline_loyalty_branch_ids" as const, statusKey: "offline_loyalty_status" as const },
  online_loyalty: { selectionKey: "online_loyalty_branch_ids" as const, statusKey: "online_loyalty_status" as const },
  ordering: null,
} as const;

const getServiceMapping = (serviceType: ServiceType) =>
  SERVICE_MAP[serviceType as keyof typeof SERVICE_MAP];

export const isServiceEnabled = (branch: IBranch, serviceType: ServiceType): boolean => {
  const mapping = getServiceMapping(serviceType);
  if (!mapping) return false;
  const status = branch[mapping.statusKey];
  return !!status && status !== PickupStatus.service_disabled;
};

export const isServiceSelected = (
  selection: BranchSelectionMap,
  branchId: string,
  serviceType: ServiceType
): boolean => {
  const mapping = getServiceMapping(serviceType);
  if (!mapping) return false;
  return !!selection[mapping.selectionKey]?.[branchId];
};

export const buildBaselineFromBranches = (
  branches: IBranch[]
): BranchSelectionMap =>
  branches.reduce<BranchSelectionMap>(
    (acc, branch) => {
      acc.pickup_branch_ids[branch.id] = isServiceEnabled(branch, "pickup");
      acc.delivery_branch_ids[branch.id] = isServiceEnabled(branch, "delivery");
      acc.drive_thru_branch_ids[branch.id] = isServiceEnabled(
        branch,
        "drive_thru"
      );
      return acc;
    },
    {
      pickup_branch_ids: {},
      delivery_branch_ids: {},
      drive_thru_branch_ids: {},
    }
  );

export const computeScheduledUntil = (
  duration: DisableDurationPreset,
  customDate?: moment.Moment | null
): { scheduledUntil: number | null; isPermanent: boolean; isScheduled: boolean } => {
  const now = moment();

  if (duration === "permanent") {
    return { scheduledUntil: null, isPermanent: true, isScheduled: false };
  }

  if (duration === "custom" && customDate) {
    const isFuture = customDate.isAfter(now);
    return {
      scheduledUntil: customDate.valueOf(),
      isPermanent: false,
      isScheduled: isFuture,
    };
  }

  // Presets are relative to the moment of selection: the end date is
  // now + the chosen duration (not the end of the current week/month).
  const durationMap = {
    "1h": { hours: 1 },
    "2h": { hours: 2 },
    "1d": { days: 1 },
    "1w": { weeks: 1 },
    "1m": { months: 1 },
  } as const;

  const unit = durationMap[duration as keyof typeof durationMap];

  return {
    scheduledUntil: now.clone().add(unit).valueOf(),
    isPermanent: false,
    isScheduled: false,
  };
};

interface BuildAuditEntriesParams {
  branches: IBranch[];
  baselineSelection: BranchSelectionMap;
  finalSelection: BranchSelectionMap;
  disableMetadata: Record<string, Partial<Record<ServiceType, IServiceDisableMetadata>>>;
  brandId: string;
  brandName: string;
  brandImage?: string;
  actor: string;
  actor_type: string;
  accountManager?: { id?: string; name?: string; email?: string; user_name?: string };
  serviceTypes: ServiceType[];
}

export const buildServiceChangeAuditEntries = ({
  branches,
  baselineSelection,
  finalSelection,
  disableMetadata,
  brandId,
  brandName,
  brandImage,
  actor,
  actor_type,
  accountManager,
  serviceTypes,
}: BuildAuditEntriesParams): IAuditLogPayload[] => {
  const entries: IAuditLogPayload[] = [];

  branches.forEach((branch) => {
    serviceTypes.forEach((serviceType) => {
      const wasSelected = isServiceSelected(baselineSelection, branch.id, serviceType);
      const isSelected = isServiceSelected(finalSelection, branch.id, serviceType);

      if (wasSelected === isSelected) {
        return;
      }

      const action: AuditAction = isSelected ? "ENABLE" : "DISABLE";
      const meta = disableMetadata[branch.id]?.[serviceType];

      const mapping = getServiceMapping(serviceType);
      if (!mapping) return;
      const beforeStateKey = mapping.statusKey;
      const before_state: Record<string, unknown> = {
        [beforeStateKey]: wasSelected ? "service_enabled" : "service_disabled",
      };
      const after_state: Record<string, unknown> = {
        [beforeStateKey]: isSelected ? "service_enabled" : "service_disabled",
      };

      entries.push({
        service_name: "order",
        action,
        actor,
        actor_type,
        service_type: serviceType,
        entity_type: "branch",
        entity_id: branch.id,
        entity_name: branch.name,
        change_area: "branch_service",
        before_state,
        after_state,
        metadata: {
          account_manager: {
            id: accountManager?.id,
            name: accountManager?.user_name || accountManager?.name || "",
            email: accountManager?.email || "",
          },
          reason: action === "DISABLE" ? meta?.reason ?? null : null,
          scheduled_at:
            action === "DISABLE" ? scheduledUntilToIso(meta?.scheduledUntil) : null,
          is_revertible: action === "DISABLE" ? meta?.is_revertible ?? false : false,
          parent_type: "store",
          parent_id: brandId,
          parent_name: brandName,
          parent_image: brandImage || "",
        },
      });
    });
  });

  return entries;
};

interface BuildDismissAllEntryParams {
  branches: IBranch[];
  baselineSelection: BranchSelectionMap;
  disableMetadata: Record<string, Partial<Record<ServiceType, IServiceDisableMetadata>>>;
  brandId: string;
  brandName: string;
  brandImage?: string;
  actor: string;
  actor_type: string;
  accountManager?: { id?: string; name?: string; email?: string; user_name?: string };
}

export const buildDismissAllEntry = ({
  branches,
  baselineSelection,
  disableMetadata,
  brandId,
  brandName,
  brandImage,
  actor,
  actor_type,
  accountManager,
}: BuildDismissAllEntryParams): IAuditLogPayload[] => {
  const enabledBranches = (serviceType: ServiceType) =>
    branches.filter((b) => isServiceSelected(baselineSelection, b.id, serviceType)).map((b) => b.id);

  const after_state: Record<string, unknown> = {
    pickup: [],
    delivery: [],
  };
  if (baselineSelection.drive_thru_branch_ids) {
    after_state.drive_thru = [];
  }

  const before_state: Record<string, unknown> = {
    pickup: enabledBranches("pickup"),
    delivery: enabledBranches("delivery"),
  };
  if (baselineSelection.drive_thru_branch_ids) {
    before_state.drive_thru = enabledBranches("drive_thru");
  }

  const reasons = [...new Set(
    branches.flatMap((b) =>
      (["pickup", "delivery", "drive_thru"] as const)
        .map((st) => disableMetadata[b.id]?.[st]?.reason)
        .filter((reason): reason is string => Boolean(reason))
    )
  )];

  const firstMeta = branches
    .flatMap((b) =>
      (["pickup", "delivery", "drive_thru"] as const)
        .map((st) => disableMetadata[b.id]?.[st])
        .filter((m): m is NonNullable<typeof m> => Boolean(m))
    )[0];

  return [
    {
      service_name: "order",
      action: "DISABLE",
      actor,
      actor_type,
      service_type: "pickup",
      entity_type: "branch",
      entity_id: uuidv4(),
      entity_name: "All Branches",
      change_area: "branch_service",
      before_state,
      after_state,
      metadata: {
        account_manager: {
          id: accountManager?.id,
          name: accountManager?.user_name || accountManager?.name || "",
          email: accountManager?.email || "",
        },
        reason: reasons[0] || "",
        scheduled_at: scheduledUntilToIso(firstMeta?.scheduledUntil),
        is_revertible: firstMeta?.is_revertible ?? false,
        parent_type: "store",
        parent_id: brandId,
        parent_name: brandName,
        parent_image: brandImage || "",
      },
    },
  ];
};

export { SERVICE_MAP };

export type ServiceDisableEntry = {
  reason: string | null;
  scheduled_at: string | null;
  is_revertible: boolean;
};

/** Epoch-ms end date -> ISO-8601 `scheduled_at`, null when permanent. */
export const scheduledUntilToIso = (
  scheduledUntil?: number | null
): string | null =>
  scheduledUntil ? moment(scheduledUntil).toISOString() : null;

/**
 * Normalizes an API/redux `scheduled_at` (ISO string, epoch seconds or
 * epoch milliseconds) to an ISO-8601 string, or null.
 */
export const normalizeScheduledAt = (
  value?: string | number | null
): string | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed =
    typeof value === "number"
      ? moment(value < 1e12 ? value * 1000 : value)
      : moment(value);
  return parsed.isValid() ? parsed.toISOString() : null;
};

export type ServiceDisablesPayload = Partial<
  Record<"pickup" | "delivery" | "drive_thru", Record<string, ServiceDisableEntry>>
>;

/**
 * Builds the `services` map expected by koinz-ordering-backend's
 * toggle-ordering-services endpoint, so it can persist and audit the
 * disable metadata (reason/scheduled_at) per branch.
 */
export const buildServiceDisablesPayload = ({
  branches,
  submittedBranchIds,
  disableMetadata,
  scheduledDisables,
}: {
  branches: IBranch[];
  submittedBranchIds: Partial<
    Record<"pickup" | "delivery" | "drive_thru", string[]>
  >;
  disableMetadata: Record<
    string,
    Partial<Record<ServiceType, IServiceDisableMetadata>>
  >;
  scheduledDisables: IScheduledServiceDisable[];
}): ServiceDisablesPayload => {
  const payload: ServiceDisablesPayload = {};
  const services = ["pickup", "delivery", "drive_thru"] as const;

  for (const service of services) {
    const submitted = submittedBranchIds[service];
    if (service === "drive_thru" && submitted === undefined) {
      continue;
    }

    const entries: Record<string, ServiceDisableEntry> = {};

    for (const branch of branches) {
      if (!isServiceEnabled(branch, service)) {
        continue;
      }
      if (submitted?.includes(branch.id)) {
        continue;
      }

      const meta =
        scheduledDisables.find(
          (s) => s.branchId === branch.id && s.serviceType === service
        ) ??
        disableMetadata[branch.id]?.[service];

      entries[branch.id] = {
        reason: meta?.reason ?? branch[`disable_reason_${service}`] ?? null,
        scheduled_at:
          scheduledUntilToIso(meta?.scheduledUntil) ??
          normalizeScheduledAt(branch[`scheduled_at_${service}`]),
        is_revertible:
          meta?.is_revertible ??
          branch[`is_revertible_${service}`] ??
          false,
      };
    }

    if (Object.keys(entries).length > 0) {
      payload[service] = entries;
    }
  }

  return payload;
};

/**
 * Builds the `scheduled_services` map for koinz-ordering-backend's
 * toggle-ordering-services endpoint.
 *
 * Existing API schedules are sent so a later enable of other branches does
 * not drop them. Redux overlays win when the user rescheduled. Re-enabled
 * branches (enableOverrides) are omitted so the backend leaves them out of
 * the disable map.
 */
export const buildScheduledServicesPayload = (
  scheduledDisables: IScheduledServiceDisable[],
  disableMetadata: Record<string, Partial<Record<ServiceType, IServiceDisableMetadata>>>,
  options?: {
    branches?: Array<{
      id: string;
      scheduled_services?: Record<string, IScheduledService>;
    }>;
    enableOverrides?: Record<string, boolean>;
  }
): ServiceDisablesPayload => {
  const payload: ServiceDisablesPayload = {};
  const services = ["pickup", "delivery", "drive_thru"] as const;
  const enableOverrides = options?.enableOverrides ?? {};

  const isOverridden = (branchId: string, service: ServiceType) =>
    enableOverrides[`${branchId}:${service}`] === true;

  for (const service of services) {
    const entries: Record<string, ServiceDisableEntry> = {};

    for (const branch of options?.branches ?? []) {
      if (isOverridden(branch.id, service)) continue;
      const existing = branch.scheduled_services?.[service];
      if (!existing) continue;
      entries[branch.id] = toServiceDisableEntry(existing);
    }

    for (const scheduled of scheduledDisables) {
      if (scheduled.serviceType !== service) continue;
      if (isOverridden(scheduled.branchId, service)) continue;
      entries[scheduled.branchId] = toServiceDisableEntry(scheduled);
    }

    for (const [branchId, meta] of Object.entries(disableMetadata)) {
      if (isOverridden(branchId, service)) continue;
      const serviceMeta = meta[service];
      if (!serviceMeta) continue;
      entries[branchId] = toServiceDisableEntry({
        reason: serviceMeta.reason,
        scheduledUntil: serviceMeta.scheduledUntil,
        is_revertible:
          serviceMeta.is_revertible ?? serviceMeta.scheduledUntil != null,
      });
    }

    if (Object.keys(entries).length > 0) {
      payload[service] = entries;
    }
  }

  return payload;
};

/**
 * Branch IDs that still have a scheduled (or metadata) disable for `service`.
 * Manual enable-overrides win so a just-enabled branch can be submitted.
 */
export const scheduledDisabledBranchIds = (
  service: ServiceType,
  scheduledDisables: IScheduledServiceDisable[],
  disableMetadata: Record<string, Partial<Record<ServiceType, IServiceDisableMetadata>>>,
  options?: {
    branches?: Array<{ id: string; scheduled_services?: Record<string, IScheduledService> }>;
    enableOverrides?: Record<string, boolean>;
  }
): Set<string> => {
  const ids = new Set<string>();
  const enableOverrides = options?.enableOverrides ?? {};

  const isOverridden = (branchId: string) =>
    enableOverrides[`${branchId}:${service}`] === true;

  for (const scheduled of scheduledDisables) {
    if (scheduled.serviceType !== service) continue;
    if (!isOverridden(scheduled.branchId)) {
      ids.add(scheduled.branchId);
    }
  }

  for (const [branchId, meta] of Object.entries(disableMetadata)) {
    if (!meta[service] || isOverridden(branchId)) continue;
    ids.add(branchId);
  }

  for (const branch of options?.branches ?? []) {
    if (isOverridden(branch.id)) continue;
    const scheduled = branch.scheduled_services?.[service];
    if (
      scheduled &&
      (scheduled.is_revertible === true || scheduled.scheduled_at != null)
    ) {
      ids.add(branch.id);
    }
  }

  return ids;
};

/**
 * Enabled IDs for toggle-ordering-services: selected in the form, but not
 * currently scheduled-disabled. Those stay out of pickup/delivery lists so
 * the backend does not treat them as a new ENABLE.
 */
export const enabledBranchIdsForOrderingToggle = (
  branchList: Array<{ id: string }>,
  selected: Record<string, boolean | undefined>,
  scheduledIds: Set<string>
): string[] =>
  branchList
    .filter((branch) => selected[branch.id] && !scheduledIds.has(branch.id))
    .map((branch) => branch.id);

export const BRANCH_SCHEDULED_SERVICE_TYPES = [
  "pickup",
  "delivery",
  "drive_thru",
  "offline_loyalty",
  "online_loyalty",
] as const;

export type BranchScheduledServiceType = typeof BRANCH_SCHEDULED_SERVICE_TYPES[number];

export type BranchScheduledServicesPayload = Partial<
  Record<BranchScheduledServiceType, ServiceDisableEntry>
>;

const toServiceDisableEntry = (source: {
  reason?: string | null;
  scheduled_at?: string | number | null;
  scheduledUntil?: number | null;
  is_revertible?: boolean;
}): ServiceDisableEntry => ({
  reason: source.reason ?? null,
  scheduled_at:
    normalizeScheduledAt(source.scheduled_at) ??
    scheduledUntilToIso(source.scheduledUntil),
  is_revertible: source.is_revertible ?? false,
});

/**
 * Builds `scheduled_services` for PUT api/manager/branch/:id.
 * That endpoint is single-branch, so the map is keyed by service
 * (`{ pickup: { reason, scheduled_at, ... } }`), not by branch id.
 */
export const buildBranchScheduledServicesPayload = ({
  branchId,
  scheduledDisables,
  disableMetadata,
  existingScheduledServices,
  branchStatuses,
}: {
  branchId: string;
  scheduledDisables: Array<{
    branchId: string;
    serviceType: string;
    reason?: string | null;
    scheduled_at?: string | number | null;
    scheduledUntil?: number | null;
    is_revertible?: boolean;
  }>;
  disableMetadata: Record<
    string,
    Partial<Record<string, IServiceDisableMetadata>>
  >;
  existingScheduledServices?: Record<string, IScheduledService> | undefined;
  branchStatuses?: Partial<Record<string, PickupStatus | undefined>>;
}): BranchScheduledServicesPayload => {
  const payload: BranchScheduledServicesPayload = {};

  if (existingScheduledServices) {
    for (const service of BRANCH_SCHEDULED_SERVICE_TYPES) {
      const existing = existingScheduledServices[service];
      if (!existing) continue;
      payload[service] = toServiceDisableEntry(existing);
    }
  }

  const branchMeta = disableMetadata[branchId] || {};

  for (const service of BRANCH_SCHEDULED_SERVICE_TYPES) {
    const scheduled = scheduledDisables.find(
      (item) => item.branchId === branchId && item.serviceType === service
    );
    const meta = branchMeta[service];
    const source = scheduled ?? meta;
    if (!source) continue;
    payload[service] = toServiceDisableEntry(source);
  }

  for (const service of BRANCH_SCHEDULED_SERVICE_TYPES) {
    const status = branchStatuses?.[service];
    if (status && status !== PickupStatus.service_disabled) {
      delete payload[service];
    }
  }

  return payload;
};
